// frontend/src/pages/games/TetrisGame.tsx

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTetrisGame } from '../../hooks/games/useTetrisGame';
import { useGameSessionTracking } from '../../hooks/games/useGameSessionTracking';
import { TetrisBoard } from '../../components/games/tetris/TetrisBoard';
import { TetrisNextPreview } from '../../components/games/tetris/TetrisNextPreview';
import { TetrisControls } from '../../components/games/tetris/TetrisControls';
import { GameCompleteModal } from '../../components/games/GameCompleteModal';
import '../../styles/games.css';
import '../../styles/games-tetris.css';

const GAME_ID = 'tetris';

export function TetrisGamePage() {
  const navigate = useNavigate();
  const {
    board,
    current,
    next,
    score,
    lines,
    level,
    isPaused,
    isGameOver,
    moveHorizontal,
    rotate,
    softDrop,
    hardDrop,
    togglePause,
    restart,
  } = useTetrisGame();

  const { recordCompletion, resetRoundTimer, lastXpEarned } = useGameSessionTracking(GAME_ID);
  const recordedRef = useRef(false);
  const linesAtRoundStartRef = useRef(0);
  const [xpEarned, setXpEarned] = useState<number | null>(null);

  useEffect(() => {
    if (isGameOver && !recordedRef.current) {
      recordedRef.current = true;
      const linesThisRound = lines - linesAtRoundStartRef.current;
      const xpAmount = linesThisRound * 10; // Tetris: temizlenen satır başına XP
      const { xpAwarded } = recordCompletion({
        score,
        cumulativeDeltas: { linesCleared: linesThisRound },
        xpAmount,
      });
      setXpEarned(xpAwarded);
    }
    if (!isGameOver) {
      recordedRef.current = false;
    }
  }, [isGameOver, lines, score, recordCompletion]);

  const handleRestart = () => {
    linesAtRoundStartRef.current = 0;
    resetRoundTimer();
    setXpEarned(null);
    restart();
  };

  return (
    <div className="game-center-page tetris-game-page">
      <header className="game-center-header">
        <div>
          <h1>🧱 Tetris</h1>
          <p>Blokları dizerek satırları temizle!</p>
        </div>
        <Link to="/games" className="game-card-btn secondary sm">
          ← Oyun Merkezi
        </Link>
      </header>

      <div className="tetris-layout">
        <div className="tetris-main">
          <div className="tetris-toolbar">
            <div className="tetris-stats">
              <span>🏆 Skor: {score}</span>
              <span>📏 Satır: {lines}</span>
              <span>⬆ Seviye: {level}</span>
            </div>
            <button type="button" className="game-card-btn secondary sm" onClick={togglePause} disabled={isGameOver}>
              {isPaused ? '▶ Devam Et' : '⏸ Duraklat'}
            </button>
          </div>

          <div className="tetris-board-wrapper">
            <TetrisBoard board={board} current={current} />
            {isPaused && !isGameOver && (
              <div className="tetris-pause-overlay">
                <div>⏸ Duraklatıldı</div>
                <button type="button" className="game-card-btn primary sm" onClick={togglePause}>
                  ▶ Devam Et
                </button>
              </div>
            )}
          </div>

          <TetrisControls
            onLeft={() => moveHorizontal(-1)}
            onRight={() => moveHorizontal(1)}
            onRotate={rotate}
            onSoftDrop={softDrop}
            onHardDrop={hardDrop}
          />
        </div>

        <aside className="tetris-sidebar">
          <TetrisNextPreview type={next} />
        </aside>
      </div>

      {isGameOver && (
        <GameCompleteModal
          emoji="🧱"
          title="Oyun Bitti"
          message={`Final Skorun: ${score} — ${lines} satır temizledin!`}
          xpEarned={xpEarned ?? lastXpEarned ?? undefined}
          onPlayAgain={handleRestart}
          onReturnToCenter={() => navigate('/games')}
        />
      )}
    </div>
  );
}
